import type { BlockPromptContext } from './types.ts';
import { buildProgressionClause, buildExampleClause, buildCrossMonthClause } from './types.ts';
import { buildPersonalizationClause } from './personalization-helpers.ts';
import { SOURCE_LANGUAGES, TARGET_LANGUAGES } from '../../constants/languages.ts';

export function buildMatchingExercisePrompt(ctx: BlockPromptContext): string {
  const source = SOURCE_LANGUAGES.find((l) => l.code === ctx.profile.native_language);
  const target = TARGET_LANGUAGES.find((l) => l.code === ctx.profile.target_language);

  return `Generate a **Matching Exercise** block for a ${ctx.profile.level.toUpperCase()} ${target?.name} learner whose native language is ${source?.name}.

## Theme
Workbook theme: "${ctx.workbookTheme}"
Sub-theme for this block: "${ctx.subTheme}"

## Block Position
This is block ${ctx.blockOrder} of ${ctx.totalBlocks}.
${buildPersonalizationClause(ctx.profile, ctx.blockOrder, ctx.isPreview)}

## Requirements

1. Choose a \`matchType\` appropriate for the sub-theme:
   - \`"question_response"\` — match questions with appropriate answers (best for conversational themes)
   - \`"word_translation"\` — match ${target?.name} words with ${source?.name} translations
   - \`"sentence_halves"\` — match sentence beginnings with endings
   - \`"word_category"\` — match words to category labels
2. Generate **7 items** for columnA and **7 items** for columnB.
3. columnA items are displayed in order (numbered 1-7). columnB items should be SHUFFLED (not aligned with their correct match).
4. \`correctPairs\` maps columnA index → columnB index (0-based). The shuffled columnB order determines the correct pairs.
5. **CRITICAL:** \`instructions\` MUST be in ${source?.name} (never in ${target?.name}). \`instructionsNative\` MUST be in ${target?.name}.
6. Difficulty must match ${ctx.profile.level.toUpperCase()}.

## Output Format

Return a single JSON object matching:

\`\`\`typescript
interface MatchingExerciseBlock {
  id: string;
  type: "matching_exercise";
  order: ${ctx.blockOrder};
  title: string;
  titleNative: string;
  theme: "${ctx.subTheme}";
  estimatedPages: 1;
  instructions: string;         // ⚠️ MUST be in ${source?.name} — NEVER in ${target?.name}
  instructionsNative: string;  // ⚠️ MUST be in ${target?.name} — brief companion version
  matchType: "word_translation" | "sentence_halves" | "question_response" | "word_category";
  columnA: string[];           // 7 items, displayed in order
  columnB: string[];           // 7 items, SHUFFLED
  correctPairs: Array<[number, number]>; // [columnA_index, columnB_index]
}
\`\`\`

Respond with ONLY the JSON object.${buildProgressionClause(ctx)}${buildExampleClause(ctx, 'matching_exercise')}${buildCrossMonthClause(ctx)}`;
}
